import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';

import { type Notification, NotificationType } from '@/entities/notification';
import {
    type Pattern,
    useGetPatternsByWatchlistIdQuery,
} from '@/entities/pattern';
import { useSelectedPatternWatchlistId } from '@/entities/pattern-watchlist';

import { PATTERN_LIVE_CANDLE_WINDOW } from '@/shared/constants/pattern';
import { formatNotificationDate } from '@/shared/lib/datetime';
import { getTimeframeInterval } from '@/shared/lib/financial';
import { useUserTimezone } from '@/shared/lib/hooks';
import { TimeframeType } from '@/shared/types/timeframe';

import PatternStudioIcon from '@/shared/assets/icons/pattern-studio.svg';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

export function useNotificationTitle(
    notification: Notification | null,
): string {
    const { t } = useTranslation();

    return useMemo(() => {
        if (!notification) return '';

        const notif = notification; // after null check, for TS narrowing

        if (notif.tipo === NotificationType.PATTERN_ALERT) {
            return `${notif.messaggio.ticker} | ${notif.messaggio.timeframe} |
                ${t('notification.title.pattern_alert')}`;
        }

        return t(`notification.title.${notif.tipo}`);
    }, [notification, t]);
}

export function useNotificationDescription(
    notification: Notification | null,
): string {
    const { t } = useTranslation();
    const [userTz] = useUserTimezone();

    return useMemo(() => {
        if (!notification) return '';

        switch (notification.tipo) {
            case NotificationType.PATTERN_ALERT:
                return t('notification.description.pattern_alert', {
                    date: dayjs(
                        notification.messaggio.timestamp_start_subpattern_live *
                            1000,
                    )
                        .tz(userTz)
                        .format('D MMMM HH:mm'),
                });
            case NotificationType.SUBSCRIPTION_WARNING:
                return t('notification.description.subscription_warning');
            default:
                return '';
        }
    }, [notification, t, userTz]);
}

export function useNotificationContent(
    notification: Notification | null,
): string {
    const { t } = useTranslation();
    const [userTz] = useUserTimezone();

    const selectedPatternWatchlistId = useSelectedPatternWatchlistId();
    const { data: patternsData } = useGetPatternsByWatchlistIdQuery(
        selectedPatternWatchlistId ?? 0,
        {
            skip: !selectedPatternWatchlistId,
        },
    );

    return useMemo(() => {
        if (!notification) return '';

        switch (notification.tipo) {
            case NotificationType.PATTERN_ALERT:
                return t('notification.content.pattern_alert', {
                    patternName:
                        (patternsData &&
                            patternsData.find(
                                (p: Pattern) =>
                                    p.unique_id ===
                                    notification.messaggio.pattern_unique_id,
                            )?.pattern_name) ||
                        notification.messaggio.pattern_unique_id,
                    timeframe: notification.messaggio.timeframe,
                    ticker: notification.messaggio.ticker,
                    date: dayjs(
                        notification.messaggio.timestamp_start_subpattern_live *
                            1000,
                    )
                        .tz(userTz)
                        .format('D MMMM HH:mm'),
                });
            case NotificationType.SUBSCRIPTION_WARNING:
                return t('notification.content.subscription_warning');
            default:
                return '';
        }
    }, [notification, t, patternsData, userTz]);
}

export function useNotificationIcon(
    notification: Notification | null,
): FunctionComponent | undefined {
    return useMemo(() => {
        switch (notification?.tipo) {
            case NotificationType.PATTERN_ALERT:
                return PatternStudioIcon;
            case NotificationType.SUBSCRIPTION_WARNING:
                return undefined;
            default:
                return undefined;
        }
    }, [notification]);
}

export function useNotificationDate(notification: Notification | null): string {
    return useMemo(() => {
        if (!notification) return '';

        return formatNotificationDate(notification.timestamp);
    }, [notification]);
}

/**
 * Checks if a pattern alert notification is still live
 * A pattern is considered live if it's within PATTERN_LIVE_CANDLE_WINDOW candles from the current time
 * @param notification - The notification to check
 * @returns true if the pattern is still live, false otherwise
 */
export function useIsPatternLive(notification: Notification | null): boolean {
    return useMemo(() => {
        if (
            !notification ||
            notification.tipo !== NotificationType.PATTERN_ALERT
        ) {
            return false;
        }

        const timeframe = notification.messaggio.timeframe as TimeframeType;
        const notificationTimestamp =
            notification.messaggio.timestamp_start_subpattern_live * 1000;

        try {
            const candleIntervalMs = getTimeframeInterval(timeframe);
            const maxTimeForLivePattern =
                PATTERN_LIVE_CANDLE_WINDOW * candleIntervalMs;
            const currentTime = Date.now();
            const timeDifference = currentTime - notificationTimestamp;

            return timeDifference <= maxTimeForLivePattern;
        } catch (error) {
            // If timeframe is not supported, consider pattern as not live
            return false;
        }
    }, [notification]);
}
